import React, { useState, useRef } from "react";
import TextFade from "../../components/CustomTexts/TextFade";
import STARS from "../../assets/stars.svg";
import NEXT from "../../assets/next.svg";
import PREV from "../../assets/prev.svg";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { Navigation, Autoplay } from "swiper/modules";
import { Testimonials } from "../../lib/constants";
import TextEffect from "../../components/CustomTexts/TextEffect";
import type { Swiper as SwiperClass } from "swiper";

const HeroTestimonials = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const swiperRef = useRef<SwiperClass | null>(null);

  const [selectedTestimonial, setSelectedTestimonial] = useState<any | null>(null);

  return (
    <div className="overflow-x-hidden px-[5%] py-20 bg-dark">
      <TextFade animation="fade-right">
        <h1 className="text-[20px] sm:text-[25px] md:text-[45px] lg:text-[50px] font-bold text-dark leading-tight">
          <TextEffect text1="Testimonials " />
        </h1>
      </TextFade>

      <div className="flex items-center gap-32 mt-20">
        {/* Left - Vertical list */}
        <div className="w-[45%] h-[45vh] md:block hidden">
          <Swiper
            modules={[Autoplay, Navigation]}
            navigation={{
              prevEl: ".hero-testimonials-prev",
              nextEl: ".hero-testimonials-next",
            }}
            spaceBetween={50}
            slidesPerView={3}
            direction={"vertical"}
            loop={true}
            autoplay={{
              delay: 2500,
              disableOnInteraction: false,
            }}
            className="w-full h-full"
          >
            {Testimonials.map((testimonial, index) => (
              <SwiperSlide key={index}>
                <div
                  onClick={() => setSelectedTestimonial(testimonial)} // ✅ open modal
                  className={`py-5 px-7 flex items-center justify-start gap-7 cursor-pointer ${
                    currentSlide === index ? "bg-secondary" : "bg-secondary/15"
                  } rounded-[12px] w-full`}
                >
                  <div className="w-[80px] h-[80px] rounded-full overflow-hidden">
                    <img
                      src={testimonial.image}
                      alt={`${testimonial.name}'s profile`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <h1 className="text-[20px] font-bold text-light">
                      {testimonial.name}
                    </h1>
                    <p className="text-[15px] font-light text-light w-[70%]">
                      {testimonial.role}
                    </p>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* Right - Main testimonial */}
        <div className="flex flex-col md:w-1/2 w-full">
          <Swiper
            modules={[Autoplay, Navigation]}
            navigation={{
              prevEl: ".hero-testimonials-prev",
              nextEl: ".hero-testimonials-next",
            }}
            spaceBetween={50}
            slidesPerView={1}
            loop={true}
            autoplay={{
              delay: 2500,
              disableOnInteraction: false,
            }}
            onSwiper={(swiper: SwiperClass) => (swiperRef.current = swiper)}
            onSlideChange={(swiper: SwiperClass) =>
              setCurrentSlide(swiper.realIndex)
            }
            className="w-full"
          >
            {Testimonials.map((testimonial, index) => (
              <SwiperSlide key={index}>
                <h1 className="text-[20px] sm:text-[25px] md:text-[49px] font-bold text-light">
                  {testimonial.name}
                </h1>
                <p className="md:hidden block text-[15px] sm:text-[18px] font-light text-light w-full">
                  {testimonial.role}
                </p>
                <img src={STARS} alt="rating" />
                <p className="md:text-[23px] text-[14px] sm:text-[18px] font-light text-light w-full mt-10 line-clamp-3">
                  {testimonial.testimony}
                </p>
                <button
                  onClick={() => setSelectedTestimonial(testimonial)} // ✅ open modal
                  className="mt-4 text-secondary underline hover:text-secondary/80"
                >
                  View More
                </button>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Custom Navigation Buttons */}
          <div className="flex items-center justify-start gap-12 mt-20">
            <img
              src={PREV}
              alt="Previous"
              className="hero-testimonials-prev cursor-pointer w-9 sm:w-11 md:w-13"
            />
            <img
              src={NEXT}
              alt="Next"
              className="hero-testimonials-next cursor-pointer w-9 sm:w-11 md:w-13"
            />
          </div>
        </div>
      </div>

  {/* ✅ Modal */}
{selectedTestimonial && (
  <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
    <div className="bg-dark p-6 sm:p-8 rounded-2xl w-full max-w-3xl relative max-h-[90vh] overflow-hidden flex flex-col">
      {/* Close Button */}
      <button
        className="absolute top-4 right-4 text-light text-2xl hover:text-secondary"
        onClick={() => setSelectedTestimonial(null)}
      >
        ✖
      </button>

      {/* Header */}
      <div className="flex items-center gap-5 mb-4">
        <img
          src={selectedTestimonial.image}
          alt={selectedTestimonial.name}
          className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover"
        />
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-light">
            {selectedTestimonial.name}
          </h2>
          <p className="text-xs sm:text-sm text-gray-300">
            {selectedTestimonial.role}
          </p>
        </div>
      </div>

      {/* Stars */}
      <img src={STARS} alt="rating" className="mb-4 w-28 sm:w-32" />

      {/* Scrollable Content */}
      <div className="overflow-y-auto pr-2 flex-1">
        <p className="text-light text-sm sm:text-[17px] leading-relaxed whitespace-pre-line">
          {selectedTestimonial.fullTestimony}
        </p>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default HeroTestimonials;
